import loadingRocketAnimation from '@/assets/video/lottie/loading-rocket.json';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ErrorComponent from '@/components/ui/custom/ErrorComponent';
import LottieWrapper from '@/components/ui/custom/LottieWrapper';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import Text from '@/components/ui/custom/dataDisplay/Text';
import BookService from '@/components/ui/custom/material/slides/book/BookService';
import useManualSingleQuery from '@/data/hook/query/v1/useManualSingleQuery';
import useCourseEditing from '@/data/hook/useCourseEditing';
import { deleteBook } from '@/data/services/bookServices';
import { booksQueryKeys } from '@/data/services/querykeys';
import Book, { bookType, BookUploadStatusEnum } from '@/models/Book';
import CoursePath from '@/models/CoursePath';
import { ApiError } from '@/models/Errors';
import Lesson from '@/models/Lesson';
import LoadingView from '@/pages/courses/LoadingView';
import { ROLES } from '@/utils/roles';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMaterialVersioning } from './Material.hooks';
import MaterialMode from './MaterialMode';
import ViewMaterial from './ViewMaterial';
import { mapTypeToMode } from '@/utils/book/bookAdapters';

interface MaterialContainerProps {
  course?: CoursePath;
  lesson: Lesson;
  blockId?: number;
  updateLessons?: (lessonId?: number) => Promise<void>;
  bookType: bookType;
  readOnly?: boolean;
  isLimitedEdition?: boolean;
}

export default function MaterialContainer({
  lesson,
  blockId,
  updateLessons,
  bookType,
  course,
  readOnly,
  isLimitedEdition,
}: MaterialContainerProps) {
  const isStudentBook = bookType === 'STUDENT';
  const initialBookId = isStudentBook ? lesson.book?.id : lesson.teacherBook;

  const [bookId, setBookId] = useState(initialBookId);

  const isValidBookId = ROLES.isValidId(bookId);

  const queryClient = useQueryClient();

  const { data: book, isInitialLoading: loadingBook } = useQuery({
    ...booksQueryKeys.get(Number(bookId)),
    enabled: !!bookId && isValidBookId,
    structuralSharing: false,
  });

  const { onEditBook, isLoading: isEditing } = useMaterialVersioning({
    lesson,
    book,
    course,
    blockId,
  });

  const { set } = useManualSingleQuery(booksQueryKeys.get(Number(bookId)));

  async function handleEditBook() {
    const { book, lesson } = await onEditBook();
    set(book);
    setBookId(book.id);
    updateLessons?.(lesson.id);

    return { book, lesson };
  }

  function invalidadeBookQuery(bookId: number) {
    queryClient.invalidateQueries({
      queryKey: booksQueryKeys.get(bookId).queryKey,
    });
  }

  function updateBook(bookId: number) {
    setBookId(bookId);
    invalidadeBookQuery(bookId);
  }

  if (loadingBook) {
    return <LoadingView />;
  }

  if (!book) {
    return (
      <ConditionalRenderer condition={!readOnly}>
        <MaterialMode
          course={course}
          lesson={lesson}
          updateLessons={updateLessons}
          bookType={bookType}
          isLoadingContainer={loadingBook}
          blockId={blockId}
          updateBook={updateBook}
        />
      </ConditionalRenderer>
    );
  }

  if (book?.chapters.length === 0) {
    return (
      <NoChaptersBookHandler
        book={book}
        lesson={lesson}
        course={course}
        loadingBook={loadingBook}
        bookType={bookType}
        updateLessons={updateLessons}
      />
    );
  }

  if (!book?.chapters[0]?.items) {
    return (
      <ViewMaterial
        book={book}
        course={course}
        lesson={lesson}
        bookType={bookType}
        readOnly={readOnly}
        onEditBook={handleEditBook}
        blockId={blockId}
        isEditing={isEditing}
        updateLessons={updateLessons}
        isLimitedEdition={isLimitedEdition}
      />
    );
  }

  return (
    <BookService
      key={book.id}
      book={book}
      readOnly
      editing={isEditing}
      lessonId={lesson.id}
      onUpdate={set}
      onEdit={handleEditBook}
      updateLessons={updateLessons}
      isLimitedEdition={isLimitedEdition}
    />
  );
}

type NoChaptersBookHandlerProps = {
  book: Book;
  updateLessons: (() => Promise<void>) | undefined;
  lesson: Lesson;
  bookType: bookType;
  course: CoursePath | undefined;
  loadingBook: boolean;
};

function NoChaptersBookHandler({
  book,
  updateLessons,
  lesson,
  bookType,
  course,
  loadingBook,
}: NoChaptersBookHandlerProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'adminPage.lesson',
  });
  const { setMaterialMode } = useCourseEditing();
  const queryClient = useQueryClient();
  const [refecthCount, setRefecthCount] = useState(0);
  const {
    data: uploadStatus,
    error,
    isInitialLoading: isLoadingUploadStatus,
  } = useQuery({
    ...booksQueryKeys.get(book.id)._ctx.uploadStatus,
    enabled: !book.chapters.length,
    refetchInterval(data, query) {
      if (data?.status !== BookUploadStatusEnum.PENDING) {
        return false;
      }
      setRefecthCount(query.state.dataUpdateCount);
      return 1_000 * 10;
    },
    retry(failureCount, error) {
      if (!(error instanceof AxiosError)) {
        return false;
      }
      const apiError = new ApiError(error);
      return apiError.statusCode !== 404 && failureCount < 2;
    },
  });

  async function tryAgain() {
    await deleteBook({ lessonId: lesson.id, bookType, bookId: book.id });
    await updateLessons?.();
    setMaterialMode('upload');
  }

  const { isPending: isPendingTryAgain, mutate: onTrygain } =
    useMutation(tryAgain);

  useEffect(() => {
    if (error) {
      setMaterialMode('slides');
      return;
    }
    if (!uploadStatus) return;

    if (uploadStatus?.status !== BookUploadStatusEnum.SUCCEEDED) return;

    queryClient.invalidateQueries({
      queryKey: booksQueryKeys.get(book.id).queryKey,
    });
    setMaterialMode(mapTypeToMode(book.materialType));
  }, [
    uploadStatus,
    error,
    setMaterialMode,
    book.id,
    book.materialType,
    queryClient,
  ]);

  if (isLoadingUploadStatus) {
    return <LoadingView />;
  }

  if (error) {
    return (
      <ConditionalRenderer condition={course}>
        {course && (
          <MaterialMode
            course={course}
            lesson={lesson}
            updateLessons={updateLessons}
            bookType={bookType}
            isLoadingContainer={loadingBook}
          />
        )}
      </ConditionalRenderer>
    );
  }

  if (uploadStatus?.status === BookUploadStatusEnum.FAILED) {
    return (
      <ErrorComponent
        hideStatusCode
        errorTextSubTitle={t('addMaterial.verifyBook')}
        errorTextTitle={t('addMaterial.errorMessageUploadZip')}
        actionButtons={[
          {
            text: t('addMaterial.tryAgain'),
            icon: <ArrowPathIcon />,
            action: () => onTrygain(),
          },
        ]}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Text
        text={t('addMaterial.waitComplete')}
        as="h4"
        className="text-primary"
      />

      <ConditionalRenderer condition={refecthCount > 10}>
        <Text text={t('addMaterial.takingTooLong')} className="text-error" />
        <MainButton
          text={t('addMaterial.tryAgain')}
          onClick={() => onTrygain()}
          color="custom"
          icon={<ArrowPathIcon />}
          className="text-primary items-center gap-1"
          loading={isPendingTryAgain}
        />
      </ConditionalRenderer>
      <div className="flex justify-center h-96 items-center">
        <LottieWrapper
          className="h-72"
          animationData={loadingRocketAnimation}
          autoplay
          loop
        />
      </div>
    </div>
  );
}
